import { AxiosError } from 'axios';
import c from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import {
  HealthFormBundle,
  useHealthFormsSequence,
} from 'hooks/useHealthFormsSequence';
import { useUrlQuery } from 'hooks/useUrlQuery';
import useProfileQuestions from 'services/general/profileQuestions';
import useSampleInfoQuestions from 'services/surveys/useSampleInfoQuestions';
import useUpdateProfileAnswer from 'services/surveys/useUpdateProfileAnswer';
import useUpdateSampleInfoAnswer from 'services/surveys/useUpdateSampleInfoAnswer';
import useFeature from 'services/features/useFeature';
import { setHomePageTinyAccountId } from 'store/home';
import { getKitById } from 'store/kits';
import {
  ProfileQuestionAnswer,
  ProfileQuestionsGroup,
} from 'types/ProfileQuestions';

import profileResumeImage from 'assets/images/profiles/profile_resume.svg';
import profileStartImage from 'assets/images/profiles/profile_start.svg';
import sampleInfoStartImage from 'assets/images/profiles/sample_info_start.svg';
import {
  ActivationScreen,
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  ContainerSizes,
  DesignButton,
  ExitFormsWarningModal,
  Header,
  HealthFormsStepBar,
  HealthFormTransition,
  LinkButton,
  Modal,
  ModalBody,
  PageContainer,
  Typography,
} from 'components';
import ProfileConfirmation from './ProfileConfirmation/ProfileConfirmation';
import { ProfileEditGroup } from './ProfileConfirmation/ProfileEditGroup';
import UnnecessarySurveysModal from './ProfileConfirmation/UnnecessarySurveysModal';
import { ProfileHeader } from './ProfileHeader/ProfileHeader';
import { ProfileQuestionItem } from './ProfileQuestion';

import styles from './Profile.module.css';

// Fake loader between bundles per ENG-4942 — same delay used between
// individual typeform surveys in TypeformSurvey.tsx.
const NEXT_BUNDLE_TRANSITION_DELAY_MS = 6000;
const PROFILE_FORM_ID = 'profile-form';

const RedirectToResultsPage = () => {
  const { currentKitId } = useCurrentKitAndTinyAccount();
  return <Navigate to={`/results/${currentKitId}`} replace />;
};

export const ProfileRoutes = () => {
  return (
    <Routes>
      <Route path='' element={<Profile />} />
      <Route
        path='/:kitId/view'
        element={<ProfileConfirmation variant='view' />}
      />
      <Route
        path='/:kitId/confirm'
        element={<ProfileConfirmation variant='confirm' />}
      />
      <Route
        path='/:kitId/edit'
        element={<ProfileConfirmation variant='edit' />}
      />
      <Route
        path='/:kitId/confirm/:questionGroupId'
        element={<ProfileEditGroup />}
      />
      <Route
        path='/:kitId/edit/:questionGroupId'
        element={<ProfileEditGroup />}
      />
      {/* This can only happen when losing the "state.from" */}
      <Route path='/:kitId' element={<RedirectToResultsPage />} />
    </Routes>
  );
};

const areTheAnswersEqual = (
  previousAnswer: string | string[] | number,
  currentAnswer: string | string[] | number,
): boolean => {
  // Convert both answers to arrays for easier comparison
  const prevArray = Array.isArray(previousAnswer)
    ? previousAnswer
    : [previousAnswer + ''];
  const currArray = Array.isArray(currentAnswer)
    ? currentAnswer
    : [currentAnswer + ''];
  // Sort arrays to ensure order doesn't matter in comparison
  const sortedPrevArray = prevArray.sort();
  const sortedCurrArray = currArray.sort();
  // Compare the lengths first
  if (sortedPrevArray.length !== sortedCurrArray.length) {
    return false;
  }
  // Compare each element
  for (let i = 0; i < sortedPrevArray.length; i++) {
    if (sortedPrevArray[i] !== sortedCurrArray[i]) {
      return false;
    }
  }
  return true;
};

export type ProfileFormData = {
  [key: string]: number | string | string[];
};
const Profile = () => {
  const tinyAccountId = useUrlQuery().get('account');
  const kitId = useUrlQuery().get('kit');
  const kit = useSelector(getKitById(kitId ?? ''));

  if (!tinyAccountId && !kitId) return <></>;
  if (tinyAccountId) {
    return <ProfileFlow tinyAccountId={tinyAccountId} />;
  }
  if (kitId) {
    return kit?.tinyaccount_id ? (
      <SampleInfoFlow tinyAccountId={kit.tinyaccount_id} kitId={kitId} />
    ) : (
      <></>
    );
  }
  return <></>;
};

const ProfileFlow = ({ tinyAccountId }: { tinyAccountId: string }) => {
  const [initialLoad, setInitialLoad] = useState(true);
  const [showProfileStartModal, setShowProfileStartModal] = useState(false);
  const [showProfileResumeModal, setShowProfileResumeModal] = useState(false);
  const { isFeatureEnabled: isRedesignedActivationEnabled } = useFeature(
    'redesigned_kit_activation_flow',
  );
  const navigate = useNavigate();
  const kitIdForReview = useUrlQuery().get('review');
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent('Health Story Survey Started', { section: 'profile' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: profileQuestions } = useProfileQuestions(
    tinyAccountId,
    kitIdForReview ?? undefined,
  );
  const profileQuestionForFlow = useMemo(() => {
    if (kitIdForReview) {
      //if this is a review, only show the questions that have not been answered
      const groupsWithNewQuestions = profileQuestions?.filter(questionGroup =>
        questionGroup.questions.some(
          question =>
            question.answer_text === '' || question.answer_text === null,
        ),
      );
      return groupsWithNewQuestions;
    }
    //continue with all the questions
    return profileQuestions;
  }, [kitIdForReview, profileQuestions]);

  const profileAnswerMutation = useUpdateProfileAnswer();
  const mutationCallback = useCallback(
    (answers: ProfileQuestionAnswer[]) => {
      return profileAnswerMutation.mutateAsync({ answers, tinyAccountId });
    },
    [profileAnswerMutation],
  );

  useEffect(() => {
    //review flow without any new question will go through to the confirm page
    if (
      kitIdForReview &&
      profileQuestions?.length &&
      !profileQuestionForFlow?.length
    ) {
      //we give a "from state" to the starting point if the user wants to go back
      navigate(`/profile/${kitIdForReview}/confirm`, {
        state: { from: `/information/${kitIdForReview}` },
      });
    }
  }, [profileQuestionForFlow]);

  useEffect(() => {
    // The redesign drops these interstitials: this bundle is now entered from
    // the health forms landing screen, which already states what it is and how
    // long it takes, so an extra "Let's create your Tiny Profile" pop-up on
    // arrival is one more click between the user and the first question.
    if (isRedesignedActivationEnabled) return;
    if (initialLoad) {
      const indexUnansweredQuestion = profileQuestionForFlow?.findIndex(
        questionGroup => !questionGroup?.questions[0]?.answer_text,
      );
      if (
        indexUnansweredQuestion !== undefined &&
        indexUnansweredQuestion >= 0
      ) {
        setInitialLoad(false);
        //decide which modal to show based on the starting question index (start or resume)
        if (indexUnansweredQuestion > 0) {
          setShowProfileResumeModal(true);
        } else {
          setShowProfileStartModal(true);
        }
      }
    }
  }, [profileQuestionForFlow, isRedesignedActivationEnabled]);

  if (!tinyAccountId) return <></>;
  return (
    <>
      <Modal controlledIsOpen={showProfileStartModal} showHeader={false}>
        <ModalBody
          image={profileStartImage}
          title={`Let’s create your Tiny Profile`}
          description={`This can be for you or another family member that you’re planning to sample with Tiny Health. We’ll ask you a few questions about your health that will help us interpret your results.`}
          onFinish={() => setShowProfileStartModal(false)}
        />
      </Modal>
      <Modal controlledIsOpen={showProfileResumeModal} showHeader={false}>
        <ModalBody
          image={profileResumeImage}
          title={`Let’s finish your profile`}
          description={`Pick up where you left off. Tell us more about you to best interpret your results.`}
          onFinish={() => setShowProfileResumeModal(false)}
        />
      </Modal>
      <THSurvey
        tinyAccountId={tinyAccountId}
        questionGroups={profileQuestionForFlow}
        mutationCallback={mutationCallback}
        bundleKey='profile'
      />
    </>
  );
};

const SampleInfoFlow = ({
  tinyAccountId,
  kitId,
}: {
  tinyAccountId: string;
  kitId: string;
}) => {
  const [initialLoad, setInitialLoad] = useState(true);
  const [showSampleInfoStartModal, setShowSampleInfoStartModal] =
    useState(false);
  const [showSampleInfoResumeModal, setShowSampleInfoResumeModal] =
    useState(false);
  const { isFeatureEnabled: isRedesignedActivationEnabled } = useFeature(
    'redesigned_kit_activation_flow',
  );
  const { data: sampleInfoQuestions } = useSampleInfoQuestions(
    tinyAccountId,
    kitId,
  );
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    trackEvent('Health Story Survey Started', { section: 'sample_info' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sampleInfoAnswerMutation = useUpdateSampleInfoAnswer();
  const mutationCallback = useCallback(
    (answers: ProfileQuestionAnswer[]) => {
      return sampleInfoAnswerMutation.mutateAsync({
        answers,
        tinyAccountId,
        kitId,
      });
    },
    [sampleInfoAnswerMutation],
  );

  useEffect(() => {
    // Same as ProfileFlow above — no arrival interstitial in the redesign.
    if (isRedesignedActivationEnabled) return;
    if (initialLoad) {
      const indexUnansweredQuestion = sampleInfoQuestions?.findIndex(
        questionGroup => !questionGroup?.questions[0]?.answer_text,
      );
      if (
        indexUnansweredQuestion !== undefined &&
        indexUnansweredQuestion >= 0
      ) {
        setInitialLoad(false);
        //decide which modal to show based on the starting question index (start or resume)
        if (indexUnansweredQuestion > 0) {
          setShowSampleInfoResumeModal(true);
        } else {
          setShowSampleInfoStartModal(true);
        }
      }
    }
  }, [sampleInfoQuestions, isRedesignedActivationEnabled]);

  if (!tinyAccountId || !kitId) return <></>;
  return (
    <>
      <Modal controlledIsOpen={showSampleInfoStartModal} showHeader={false}>
        <ModalBody
          image={sampleInfoStartImage}
          title={`Let’s complete your sample information`}
          description={`We’ll ask you a few questions about the sample user’s health. We can’t publish your results without it.`}
          onFinish={() => setShowSampleInfoStartModal(false)}
        />
      </Modal>
      <Modal controlledIsOpen={showSampleInfoResumeModal} showHeader={false}>
        <ModalBody
          image={profileResumeImage}
          title={`Let’s finish your sample information`}
          description={`Pick up where you left off. Tell us more about the sample user’s current health. We can’t publish your results without it.`}
          onFinish={() => setShowSampleInfoResumeModal(false)}
        />
      </Modal>
      <THSurvey
        tinyAccountId={tinyAccountId}
        questionGroups={sampleInfoQuestions}
        mutationCallback={mutationCallback}
        bundleKey='sampleInfo'
        kitId={kitId}
      />
    </>
  );
};

export const THSurvey = ({
  tinyAccountId,
  questionGroups,
  mutationCallback,
  modalProps,
  isSingleEdit = false,
  bundleKey,
  kitId,
}: {
  tinyAccountId: string;
  questionGroups?: ProfileQuestionsGroup[] | null;
  mutationCallback?: (answers: ProfileQuestionAnswer[]) => Promise<void>;
  modalProps?: React.ComponentProps<typeof UnnecessarySurveysModal>;
  isSingleEdit?: boolean;
  bundleKey?: 'profile' | 'sampleInfo';  
  kitId?: string;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const kitIdFromState = (state?.from as string | undefined)?.match(
    /^\/information\/([^/?]+)/,
  )?.[1];
  const currentKitId = kitId ?? kitIdFromState;
  const [initialLoad, setInitialLoad] = useState(true);
  const [startingQuestionIdx, setStartingQuestionIdx] = useState(0);
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [isExitModalOpen, setIsExitModalOpen] = useState(false);
  const [nextBundleToShow, setNextBundleToShow] =
    useState<HealthFormBundle | null>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const kitIdForReview = useUrlQuery().get('review');
  const { isFeatureEnabled: isRedesignedActivationEnabled } = useFeature(
    'redesigned_kit_activation_flow',
  );
  const { bundles, currentIndex: currentBundleIndex } = useHealthFormsSequence(
    tinyAccountId,
    currentKitId,
  );
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormData>();
  const currentAnswers = watch();

  useEffect(() => {
    return () => clearTimeout(transitionTimeoutRef.current);
  }, []);

  // The list links here with a push, so popping it returns to that list with
  // its own state; pushing state.from again would stack list/edit entries and
  // make the browser back bounce between them.
  // With nothing behind this entry (opened or reloaded here) -1 is a no-op, so
  // fall back to pushing the list. react-router keeps the entry index in
  // history.state.
  const leaveSingleEdit = () => {
    if (!state?.from) {
      navigate('/');
    } else if ((window.history.state?.idx ?? 0) > 0) {
      navigate(-1);
    } else {
      navigate(state.from, { replace: true });
    }
  };

  const onSubmit: SubmitHandler<ProfileFormData> = async formData => {
    //Validation for 'In the past month' section only
    const pastMonthQuestions = questionGroups
      ?.find(qg => qg.lookup_code === 'past_month')
      ?.questions.map(q => q.id);

    const needsValidation = Object.keys(formData).filter(key =>
      pastMonthQuestions?.includes(Number(key)),
    );

    if (needsValidation.length > 0) {
      const sum = needsValidation.reduce(
        (acc, key) => acc + Number(formData[key]),
        0,
      );
      if (sum !== 100) {
        toast.error('Sum must total to 100');
        return;
      }
    }

    const updatedAnswers = Object.keys(formData).reduce(
      (acc: ProfileQuestionAnswer[], key: string) => {
        const question_id = Number(key);
        const answer_text = formData[key];
        if (pastMonthQuestions?.includes(question_id)) {
          return [...acc, { question_id, answer_text }];
        }
        const currentQuestion = currentPage?.questions.find(
          question => question.id === question_id,
        );
        if (currentQuestion) {
          const previousAnswer = currentQuestion.answer_text;
          if (!areTheAnswersEqual(previousAnswer, answer_text)) {
            //only add the question if the answer has changed
            return [...acc, { question_id, answer_text }];
          }
        }
        //skip the question if the answer hasn't changed
        return acc;
      },
      [],
    );

    if (mutationCallback) {
      try {
        if (updatedAnswers?.length) {
          await mutationCallback(updatedAnswers);
        }
        if (questionGroups?.length) {
          if (currentQuestionIdx < questionGroups.length - 1) {
            //move to the next screen
            setCurrentQuestionIdx(currentQuestionIdx + 1);
          } else {
            //finish the survey and redirect
            if (kitIdForReview) {
              //if this is a review, redirect to the confirmation page to check the previously answered questions
              navigate(`/profile/${kitIdForReview}/confirm`);
            } else if (isSingleEdit) {
              // Without the flag a single edit stays on the page; with it the
              // user goes back to the list they came from.
              if (isRedesignedActivationEnabled && state?.from) {
                leaveSingleEdit();
              }
            } else if (isRedesignedActivationEnabled && bundleKey && currentKitId) {
              // Auto-progress straight into the next health-form bundle
              // instead of dropping the user back on the landing screen;
              // show what's next for a beat first.
              const bundleIndex = bundles.findIndex(
                bundle => bundle.key === bundleKey,
              );
              const nextBundle = bundles
                .slice(bundleIndex + 1)
                .find(bundle => !bundle.done);
              if (nextBundle) {
                setNextBundleToShow(nextBundle);
                transitionTimeoutRef.current = setTimeout(() => {
                  navigate(nextBundle.href, { replace: true });
                }, NEXT_BUNDLE_TRANSITION_DELAY_MS);
              } else {
                // Every health-form bundle is done — show the activation
                // completion screen instead of the forms landing screen.
                navigate(`/information/${currentKitId}/complete`);
              }
            } else if (state?.from) {
              navigate(state.from);
            } else {
              dispatch(setHomePageTinyAccountId(tinyAccountId));
              navigate('/');
            }
            if (!isRedesignedActivationEnabled || isSingleEdit) {
              toast.success('Your answers have been saved');
            }
          }
        }
      } catch (error) {
        if (!(error instanceof AxiosError && error.response?.status === 409)) {
          toast.error('Something went wrong, please try again');
        }
      }
    }
  };

  useEffect(() => {
    if (initialLoad) {
      const indexUnansweredQuestion = questionGroups?.findIndex(
        questionGroup => !questionGroup?.questions[0]?.answer_text,
      );
      if (
        indexUnansweredQuestion !== undefined &&
        indexUnansweredQuestion >= 0
      ) {
        setInitialLoad(false);
        setStartingQuestionIdx(indexUnansweredQuestion);
        setCurrentQuestionIdx(indexUnansweredQuestion);
      }
    }
  }, [questionGroups]);

  const currentPage = useMemo(
    () =>
      questionGroups?.length && questionGroups[currentQuestionIdx]
        ? questionGroups[currentQuestionIdx]
        : null,
    [questionGroups, currentQuestionIdx],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [questionGroups]);

  if (!questionGroups || !currentPage) return <></>;
  // Without a resolvable currentKitId there's no href to drive auto-progression
  // into (see onSubmit below, which requires it too) — keep the whole redesigned
  // chrome off rather than showing progress UI that the submit handler can't
  // follow through on.
  const ownBundle =
    bundleKey &&
    currentKitId &&
    bundles.find(bundle => bundle.key === bundleKey);
  const bundleProgress = nextBundleToShow
    ? 1
    : questionGroups.length
      ? currentQuestionIdx / questionGroups.length
      : 0;
  /* No goBack means ProfileHeader renders its blank spacer. Question 0
     has no previous question, so the only thing back can do there is
     leave the form — which is a redesign affordance: off the flag the
     user still has MobileNav to get out, and showing a back button
     would change the legacy flow. */
  const goBack =
    nextBundleToShow ||
    (currentQuestionIdx === 0 && !isRedesignedActivationEnabled)
      ? undefined
      : () => {
          if (currentQuestionIdx > 0) {
            setCurrentQuestionIdx(current => current - 1);
            return;
          }
          setIsExitModalOpen(true);
        };
  const profileHeader = (
    <ProfileHeader
      currentQuestionIdx={nextBundleToShow ? 0 : currentQuestionIdx}
      questions={questionGroups}
      goBack={goBack}
      hideProgress={
        !!nextBundleToShow || (isRedesignedActivationEnabled && !!ownBundle)
      }
      redesigned={isRedesignedActivationEnabled}
    />
  );
  const hasChanges = currentPage.questions.some(question => {
    const value = currentAnswers[question.id];
    if (value === undefined) return false;
    const copy = <T,>(answer: T): T =>
      Array.isArray(answer) ? ([...answer] as T) : answer;
    return !areTheAnswersEqual(
      copy(question.answer_text ?? ''),
      copy(value),
    );
  });
  const backToList = () => leaveSingleEdit();
  if (isRedesignedActivationEnabled) {
    const stepBar = !kitIdForReview && ownBundle && (
      <HealthFormsStepBar
        total={bundles.length}
        currentIndex={currentBundleIndex}
        currentProgress={bundleProgress}
        label={ownBundle.label}
        className={styles.activationStepBar}
      />
    );

    return (
      <>
        {modalProps && <UnnecessarySurveysModal {...modalProps} />}
        <ActivationScreen
          className={styles.activationScreen}
          // Without a bundle (no resolvable kit) ProfileHeader keeps drawing its
          // own progress markers.
          onBack={isSingleEdit ? backToList : ownBundle ? goBack : undefined}
          header={
            isSingleEdit ? (
              <div className={styles.singleEditHeader}>
                <Header title='Edit your answers' />
              </div>
            ) : ownBundle ? (
              stepBar
            ) : (
              profileHeader
            )
          }
          scrollResetKey={currentQuestionIdx}
          footer={
            !nextBundleToShow && (
              <div className={styles.activationButtons}>
                <DesignButton
                  label={isSingleEdit ? 'Save' : 'Continue'}
                  type='submit'
                  form={PROFILE_FORM_ID}
                  loading={isSubmitting}
                  disabled={isSingleEdit && !hasChanges}
                  size={ButtonSizes.M}
                  color={ButtonColors.PURPLE}
                  width={'full'}
                />
                <div className={c('flex justify-center', styles.textAction)}>
                  <DesignButton
                    label='Finish later'
                    variant={ButtonVariants.TEXT}
                    size={ButtonSizes.M}
                    color={ButtonColors.DARK}
                    disabled={isSubmitting}
                    onClick={() => setIsExitModalOpen(true)}
                  />
                </div>
              </div>
            )
          }
        >
          <PageContainer
            size={ContainerSizes.SM}
            className={styles.activationBody}
          >
            {nextBundleToShow ? (
              <HealthFormTransition
                nextLabel={nextBundleToShow.label}
                nextTimeEstimate={nextBundleToShow.timeEstimate}
                nextDescription={nextBundleToShow.description}
              />
            ) : (
              <div className={styles.infoContainer}>
                {!!currentPage.title && (
                  <Typography variant='heading-xl'>
                    {currentPage.title}
                  </Typography>
                )}
                {!!currentPage.subtitle && (
                  <Typography variant='body-s' className='grey-text'>
                    {currentPage.subtitle}
                  </Typography>
                )}
                <form
                  id={PROFILE_FORM_ID}
                  className={styles.formContainer}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  {currentPage.questions.map(question => (
                    <ProfileQuestionItem
                      key={question.id}
                      question={question}
                      register={register}
                      errors={errors}
                      control={control}
                    />
                  ))}
                </form>
              </div>
            )}
          </PageContainer>
        </ActivationScreen>
        <ExitFormsWarningModal
          isOpen={isExitModalOpen}
          variant='survey'
          onCancel={() => setIsExitModalOpen(false)}
          onConfirm={() => navigate(state?.from ? state.from : '/')}
        />
      </>
    );
  }

  return (
    <>
      {modalProps && <UnnecessarySurveysModal {...modalProps} />}
      {isSingleEdit ? (
        !nextBundleToShow && <Header title='Edit your answers' />
      ) : (
        profileHeader
      )}
      <PageContainer
        size={ContainerSizes.SM}
        className={styles.profileContainer}
      >
        {nextBundleToShow ? (
          <HealthFormTransition
            nextLabel={nextBundleToShow.label}
            nextTimeEstimate={nextBundleToShow.timeEstimate}
            nextDescription={nextBundleToShow.description}
          />
        ) : (
          <div className={styles.infoContainer}>
            {!!currentPage.title && (
              <Typography variant='heading-xl'>{currentPage.title}</Typography>
            )}
            {!!currentPage.subtitle && (
              <Typography variant='body-s' className='grey-text'>
                {currentPage.subtitle}
              </Typography>
            )}
            <form
              className={styles.formContainer}
              onSubmit={handleSubmit(onSubmit)}
            >
              {currentPage.questions.map(question => (
                <ProfileQuestionItem
                  key={question.id}
                  question={question}
                  register={register}
                  errors={errors}
                  control={control}
                />
              ))}
              <div className={styles.buttons}>
                <DesignButton
                  label={isSingleEdit ? 'Save' : 'Continue'}
                  type='submit'
                  loading={isSubmitting}
                  size={ButtonSizes.M}
                  color={ButtonColors.PURPLE}
                  width={'full'}
                  className='mt-2'
                />
                {!isSingleEdit && (
                  <div className='flex justify-center'>
                    <LinkButton
                      label='Finish later'
                      className='mt-2'
                      href={state?.from ? state.from : '/'}
                      variant={ButtonVariants.TEXT}
                      size={ButtonSizes.M}
                      color={ButtonColors.DARK}
                    />
                  </div>
                )}
              </div>
            </form>
          </div>
        )}
      </PageContainer>
    </>
  );
};

export default Profile;
